import axiosLib, { AxiosError, AxiosRequestConfig } from "axios";
import { MaxRetriesError, SessionExpiredError } from "~/helpers/errors";
import {
  getJWT,
  setJWT,
  getRefreshToken,
  setRefreshToken,
  isTokenExpired,
} from "~/helpers/auth";
import { paths } from "~/router/paths";
import { isGuestPath } from "~/constants/paths";
import { endpointsToExcludeTheJWT } from "~/api/constants";
import { getNow, trackApiRequest } from "~/helpers/datadog";
import { triggerForceSignOut } from "~/helpers/customEvents";
import { noteFailure, noteSuccess } from "~/helpers/maintenance";
const apiURL: string = import.meta.env.VITE_API_URL;

export const axios = axiosLib.create({
  timeout: 60000,
  // Lets the browser send/receive the HttpOnly auth cookie the API now also
  // sets alongside the JSON-body tokens below (security ticket: auth tokens
  // must not be exfiltratable from localStorage via XSS).
  withCredentials: true,
});

const getURL = (path: string) =>
  `${apiURL}/${path.endsWith("/") ? path : path + "/"}`;

type DatadogAxiosConfig = AxiosRequestConfig & {
  metadata?: {
    requestStartTime: number;
  };
};

const getRequestDuration = (config?: AxiosRequestConfig) => {
  const requestStartTime = (config as DatadogAxiosConfig | undefined)?.metadata
    ?.requestStartTime;

  if (!requestStartTime) return 0;
  return getNow() - requestStartTime;
};

// Sign-in, registration, password reset and refresh carry no token, so a 401
// from them is the answer itself (wrong password, dead refresh token) and
// must reach the caller unchanged.
const isJWTExcludedEndpoint = (url?: string): boolean =>
  !!url && endpointsToExcludeTheJWT.some((endpoint) => url.includes(endpoint));

// The one in-flight refresh. Every caller that needs a fresh access token
// awaits this same promise, so a page-load batch of N requests produces one
// refresh call rather than N (Monday ticket 12857922247).
let inFlightRefresh: Promise<string | null> | null = null;

const requestNewAccessToken = async (): Promise<string | null> => {
  const refresh = getRefreshToken();
  if (!refresh) return null;

  try {
    // A bare axios call on purpose. Going through sendPost would re-enter
    // handleErrorWrapper, which retries and signs out on its own — making the
    // outcome of a refresh impossible to reason about from here, and nesting
    // one retry budget inside another.
    const res = await axios.post(getURL("auth/token/refresh/"), { refresh });
    const access = res?.data?.access;
    if (!access) return null;

    setJWT(access);
    return access;
  } catch {
    // Includes the 401 that means the refresh token is itself dead. Callers
    // treat null as "stop, this cannot be retried".
    return null;
  }
};

/**
 * Fresh access token, refreshing at most once across concurrent callers.
 *
 * Resolves to null when the refresh was refused, which is not retryable: the
 * caller's job is then to sign the user out, not to try again.
 */
export const ensureFreshToken = (): Promise<string | null> => {
  if (!inFlightRefresh) {
    inFlightRefresh = requestNewAccessToken().finally(() => {
      inFlightRefresh = null;
    });
  }

  return inFlightRefresh;
};

const handleRequest = async (config: AxiosRequestConfig): Promise<any> => {
  (config as DatadogAxiosConfig).metadata = { requestStartTime: getNow() };

  const isAuthEndpoint = isJWTExcludedEndpoint(config.url);
  let isS3Endpoint = false;
  try {
    if (config.url) {
      const hostname = new URL(config.url).hostname;
      isS3Endpoint =
        hostname === "s3.amazonaws.com" ||
        hostname.endsWith(".s3.amazonaws.com") ||
        hostname.endsWith(".amazonaws.com");
    }
  } catch {
    isS3Endpoint = false;
  }

  if (isS3Endpoint || isAuthEndpoint) {
    return config;
  }

  let jwt = getJWT();

  // Refresh ahead of the request rather than after a 401 comes back. On a page
  // load every request in the batch reaches here holding the same expired
  // token; ensureFreshToken collapses them onto one refresh, so the batch
  // produces no 401s at all instead of one per request.
  if (isTokenExpired(jwt) && getRefreshToken()) {
    jwt = await ensureFreshToken();

    if (!jwt) {
      // The refresh token was refused. No retry can help, so stop here and
      // send the user to login rather than letting the batch spin on 401s.
      forceSignOut();
      throw new SessionExpiredError();
    }
  }

  if (jwt && config.headers) config.headers.Authorization = `Bearer ${jwt}`;
  return config;
};

axios.interceptors.request.use(handleRequest);
axios.interceptors.response.use(
  (response) => {
    trackApiRequest({
      method: response.config.method,
      url: response.config.url,
      status: response.status,
      durationMs: getRequestDuration(response.config),
    });

    noteSuccess();
    return response;
  },
  (error: AxiosError) => {
    trackApiRequest({
      method: error.config?.method,
      url: error.config?.url,
      status: error.response?.status,
      durationMs: getRequestDuration(error.config),
      errorMessage: error.message,
    });

    // Fire and forget — outage detection must never delay or replace the
    // rejection. A no-status error (error.response undefined) falls straight
    // through to Promise.reject below untouched; every auth branch further
    // down in handleErrorWrapper is separately gated on err.response being
    // present, so this addition cannot disturb the token-refresh /
    // forceSignOut / MaxRetriesError flows.
    void noteFailure(error.response?.status, error.config?.url);

    return Promise.reject(error);
  }
);

/**
 * Ends a dead session: drops the tokens everywhere, then sends the user to
 * login unless they are already on a guest page. Returns true when a redirect
 * was started.
 */
export const forceSignOut = (): boolean => {
  // Tokens go first, on every page, so route guards and the next load see the
  // signed-out state even where no redirect follows. Checkout and settings
  // used to be skipped entirely, which left an expired session sitting on a
  // page that could no longer load anything.
  setJWT("");
  setRefreshToken("");

  if (isGuestPath(window.location.pathname)) {
    return false; // Already where a logged-out user belongs.
  }

  // SPA navigation when a page wrapper is listening (an abrupt teardown can
  // throw removeChild errors from portals and third-party scripts); a full
  // navigation when nothing is mounted to take the event.
  if (!triggerForceSignOut()) {
    window.location.href = paths.auth.login;
  }
  return true;
};

// Every sign-out path rejects with the same error, so callers can tell an
// ended session from a real failure and not log it as one.
const endSession = (): never => {
  forceSignOut();
  throw new SessionExpiredError();
};

const handleErrorWrapper = async <T = any>(
  next: Function,
  retries: number = 5
): Promise<T | void> => {
  if (retries <= 0) {
    throw new MaxRetriesError("Max retries reached");
  }

  try {
    return await next();
  } catch (err: unknown) {
    if (err instanceof AxiosError) {
      if (err.response) {
        const { status } = err.response;
        const refresh = getRefreshToken();

        if (status === 402) {
          localStorage.setItem("plansModal", "true");
        }

        if (status === 401 && !isJWTExcludedEndpoint(err.config?.url)) {
          if (err.response.data?.code === "user_not_found") {
            return endSession();
          }

          if (refresh) {
            // Shared with every other request that raced into this branch, so
            // one expired token still costs exactly one refresh call.
            const access = await ensureFreshToken();
            if (!access) return endSession();

            return handleErrorWrapper(next, retries - 1);
          }

          // No refresh token to try: the session is over, whichever endpoint
          // answered. (A bare "/auth/" check used to exempt auth/user/ here,
          // so a dead session kept polling and logging instead of leaving.)
          return endSession();
        }
      }
    }
    return Promise.reject(err);
  }
};

export const sendGet = async (path: string, params?: Object): Promise<any> => {
  return handleErrorWrapper(async () => {
    return axios.get(getURL(path), { params });
  });
};

export const sendPost = async (path: string, params?: Object) => {
  return handleErrorWrapper(async () => {
    return axios.post(getURL(path), params);
  });
};

export const sendPut = async (path: string, params?: Object) => {
  return handleErrorWrapper(async () => {
    return axios.put(getURL(path), params);
  });
};

export const sendDelete = async (path: string, params: Object) => {
  return handleErrorWrapper(async () => {
    return axios.delete(getURL(path), params);
  });
};
